.root {
  position: relative;
  display: inline-flex;
}

/* The designed trigger is a 40x40 bordered icon button holding the funnel glyph. It fills a slot
   that sizes it — `Table` squares it to the search field's height — and is 40x40 anywhere else
   (a percentage of an auto-sized box resolves to auto, leaving the minimum). */
.trigger {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 2.5rem;
  min-height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--surface);
  color: var(--text-secondary);
  cursor: pointer;
}

.trigger:hover {
  background: var(--surface-hover);
}

.trigger:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

/* The applied state is the dot alone — the design shows no chip row and no other summary. */
.dot {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--warning-500);
}

.popup {
  min-width: 38rem;
  max-width: min(38rem, calc(100vw - 2rem));
}

.empty {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1.25rem 1rem;
  text-align: center;
}

.empty-title {
  color: var(--text-secondary);
}

.empty-text {
  color: var(--text-quaternary);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
}

.row {
  display: grid;
  /* Field, operator, value, remove — the four controls the design puts on one desktop line. */
  grid-template-columns: 1fr 1fr 1fr auto;
  align-items: center;
  gap: 0.5rem;
}

/*
 * A date range is two bounds sharing the single value cell, so the row keeps the four columns the
 * design draws rather than growing a fifth that every other field type would leave empty.
 *
 * They stack rather than sit side by side. A native date input needs about 7rem for `dd.mm.yyyy`
 * and its calendar button; half a value cell is nearer 6rem, which clipped the year — and widening
 * the popup past the designed 38rem to fit one field type is the worse trade. Stacking gives each
 * bound the whole cell and costs one row of height on the only field type that has two controls.
 */
.range {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.25rem;
  min-width: 0;
}

.range > * {
  min-width: 0;
}

.range input {
  min-width: 0;
  width: 100%;
}

/* A stacked range is two controls tall, so the row's other cells align to its top rather than its
   middle — an operator select floating beside the gap between two dates reads as unrelated. */
.row-range {
  align-items: start;
}

/* The sheet header the design gives the mobile presentation only. */
.sheet-head {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
}

.sheet-copy {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
}

.sheet-text {
  color: var(--text-quaternary);
}

/* Below the breakpoint the popup is a sheet, and the design stacks each group over two lines. */
@media (max-width: 767.98px) {
  .popup {
    min-width: 0;
  }

  /* The design stacks each group over two lines: field and operator, then value and remove. */
  .row {
    grid-template-columns: 1fr 1fr;
    row-gap: 0.5rem;
  }

  .row > *:nth-child(3) {
    grid-column: 1;
  }

  /* Dotted rules separate consecutive groups, as the sheet frames show. */
  .row + .row {
    padding-top: 0.75rem;
    border-top: 1px dashed var(--border-light);
  }
}

.remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.375rem;
  background: none;
  /* `--gray-400` is 2.52:1 on white, under the 3:1 WCAG 2.2 §1.4.11 asks of a meaningful glyph, and
     a remove control is meaningful. See the icon-colour note in `apps/admin/CLAUDE.md`. */
  color: var(--text-quaternary);
  cursor: pointer;
}

.remove:hover {
  background: var(--surface-hover);
  color: var(--text-tertiary);
}

.remove:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
